import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import DashboardMenu from "./DashboardMenu";
import StaffNotificationsPrompt from "./StaffNotificationsPrompt";
import ImpersonationBanner, { leaveImpersonation } from "./ImpersonationBanner";
import { getUserInfoFromToken } from "../../store/action";
import {
  resolveOrganizationSetupComplete,
  ORGANIZATION_SETUP_COMPLETE_EVENT,
} from "../../utils/organizationSetup";

/**
 * Dashboard Component - Main layout for logged-in users
 */
const Dashboard = ({ children, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Defaults to complete (fail open) — only org-owner-capable roles ever get
  // this flipped to false, and only once the fetch below actually resolves.
  const [isSetupComplete, setIsSetupComplete] = useState(true);

  useEffect(() => {
    // Read identity from the token rather than the USER_EMAIL key: the role
    // decides which tabs render, and it should come from the same signed blob
    // the API checks. Fall back to USER_EMAIL only for the display name.
    const info = getUserInfoFromToken();
    const email = info?.email || localStorage.getItem("USER_EMAIL");
    if (email || info) {
      setUser({
        email,
        role: info?.role,
        impersonating: info?.impersonatorId != null,
      });
    }

    // Gates the setup-locked nav items for a CompanyAdmin — the only role that
    // owns an organization. No-ops (stays `true`) for every other role,
    // platform staff included, and fails open on error.
    resolveOrganizationSetupComplete(info?.role).then(setIsSetupComplete);

    setLoading(false);
  }, []);

  // The fetch above only runs on mount, and the setup wizard finishes with
  // this sidebar on screen beside it. The event is what unlocks the gated
  // items there and then; without it they stay locked until the next full
  // page load.
  useEffect(() => {
    const handleSetupComplete = () => {
      resolveOrganizationSetupComplete(getUserInfoFromToken()?.role).then(
        setIsSetupComplete,
      );
    };
    window.addEventListener(
      ORGANIZATION_SETUP_COMPLETE_EVENT,
      handleSetupComplete,
    );
    return () => {
      window.removeEventListener(
        ORGANIZATION_SETUP_COMPLETE_EVENT,
        handleSetupComplete,
      );
    };
  }, []);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate("/");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50">
        <div
          role="status"
          aria-label={t("Dashboard.Menu.loading")}
          className="size-12 animate-spin rounded-full border-4 border-ink-200 border-t-brand-600 motion-reduce:animate-none"
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-ink-50">
      <DashboardMenu
        user={user}
        onLogout={handleLogout}
        onExitImpersonation={leaveImpersonation}
        isSetupComplete={isSetupComplete}
      />
      {/* `clip`, not `hidden`: hidden also makes <main> a scroll container
          that never scrolls (the window does), and every `sticky` inside it
          — the setup wizard's Continue bar — would stop sticking. */}
      <main className="min-w-0 flex-1 overflow-x-clip lg:ml-70">
        <div className="w-full max-w-[1400px] px-4 pt-20 pb-4 sm:px-5 sm:pb-5 lg:p-8">
          {/* In the chrome, not on a page: the point of the channel is being
              told about a booking while looking at something else. It renders
              nothing for roles or browsers that cannot use it, and starts the
              polling either way once permission is granted. */}
          <ImpersonationBanner />
          <StaffNotificationsPrompt />
          {children}
        </div>
      </main>
    </div>
  );
};

Dashboard.propTypes = {
  children: PropTypes.node,
  onLogout: PropTypes.func,
};

export default Dashboard;
